/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import React from 'react';
import { Route, BrowserRouter as Router, Routes, Navigate } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { IAuthState, Logout } from '../components/auth';
import { RootState } from '../../setup';
import { MasterInit } from '../layout/MasterInit';
import { Login, STATUSES } from '../views/login/Login';
import { ConfirmAccount } from '../views/confirm-account/ConfirmAccount';
import { ConfirmForgotPassword } from '../views/confirm-forgot-password/ConfirmForgotPassword';
import { ForgotPassword } from '../views/forgot-password/ForgotPassword';
import { ConfirmApplication } from '../components/auth/confirm-application/ConfirmApplication';
import Dashboard from '../views/dashboard/Dashboard';
import { NotFound } from '../components/not-found/NotFound';
import { MerchantAgreement } from '../views/agreements/MerchantAgreement';
import { ReleaseNotes } from '../views/release-notes/ReleaseNotes';
import { Notifications } from '../views/notifications/Notifications';
import { Profile } from '../views/profile/Profile';
import { PageContextProvider } from '../store/page-context';
import { MerchantsPage } from '../views/merchants/list/MerchantsPage';
import { ApplicationsPage } from '../views/application/ApplicationsPage';
import { ErrorBoundaryPage } from '../components/error-boudary/ErrorBoundaryPage';
import { PageTitle } from '../layout/core';
import { BatchDetails } from '../components/batches/BatchDetails';
import { IdentityProvider } from '../store/identyti/identity-context';
import { SalesAgenciesList } from '../views/agencies/SalesAgenciesList';
import { AgencyDetails } from '../components/agents/AgencyDetails';
import { CustomerPage } from '../views/customer/CustomerPage';
import { RiskPage } from '../views/risk/RiskPage';
import { OrderDetails } from '../components/orders/OrderDetails';
import { MerchantPage } from '../views/merchants/list/MerchantPage';
import { ActivityLog } from '../components/orders/ActivityLog';
import { OrdersList } from '../views/orders/OrdersList';
import { CLAIMS, checkRoles } from '../utils/roles-helper';
import { NewApplication } from '../components/application/NewApplication';
import { Logs } from '../views/developers/Logs';
import { useTranslation } from 'react-i18next';
import { FeeConfiguration } from '../views/default-fee/FeeConfiguration';
import { BusinessSettings } from '../views/user-settings/BusinessSettings';
import { ProfileSettings } from '../views/user-settings/ProfileSettings';
import { UsersList } from '../components/users/UsersList';
import { SettlementsDetails } from '../components/settlements/SettlementsDetails';
import { DisputesPage } from '../views/disputes/DisputesPage';
import { DisputesQueuePage } from '../views/disputes/DisputesQueuePage';
import { DisputesQueueDetails } from '../components/disputes/disputes_queues/disputes_queue_details/DisputesQueueDetails';
import { DisputesArchivedPage } from '../views/disputes/DisputesArchivedPage';
import { ContactsList } from '../views/contacts/ContactsList';
import { PartnersList } from '../views/partners/PartnersList';
import { Accounts } from '../views/accounts/Accounts';
import { AccountDetails } from '../components/accounts/AccountDetails';
import { DisputesQueueItem } from '../components/disputes/disputes_queues/DisputesQueueItem';
import { UserDetails } from '../components/users/UserDetails';
import { UserDetailsInformation } from '../components/users/UserDetailsInformation';
import { USER_TABS } from '../constants/users/userModalUtils';
import { UserDetailsSecurity } from '../components/users/UserDetailsSecurity';
import { UserDetailsAccount } from '../components/users/UserDetailsAccount';
import { ContactDetails } from '../components/contacts/ContactDetails';
import { CommissionsPage } from '../views/agencies/CommissionsPage';
import { DealsPage } from '../views/agencies/DealsPage';
import { PartnersDealsDetailsPage } from '../components/agents/PartnersDealsDetailsPage';
import { AgenciesDealsDetailsPage } from '../components/agents/AgenciesDealsDetailsPage';
import { PartnerCommissionsDetailsPage } from '../components/agents/PartnerCommissionsDetailsPage';
import { AgencyCommissionsDetailsPage } from '../components/agents/AgencyCommissionsDetailsPage';
import { PartnerDetails } from '../components/partners/PartnerDetails';
import { FundsReservePage, fundsBreadCrumbs } from '../views/funds-reserve/FundsReservePage';
// import { FundsReserveDetails } from '../components/funds-reserve/FundsReserveDetails';
import { FundsReserveConfigPage } from '../views/funds-reserve-config/FundsReserveConfigPage';
import AnalyticsPage from '../views/analytics/Analytics';
import { RoutingLayout } from './RoutingLayout';
import { adminRoutes } from './adminRoutes';
import { merchantRoutes } from './merchantRoutes';
import { developersRoutes } from './developersRoutes';
import { FinancialAccount } from '../views/financial_accaunt/FinancialAccount';
import { partnerDetailsRoutes } from './partnerDetailsRoutes';
import { SignUp } from '../views/sign-up/SignUp';
import { MerchantsProvider } from '../store/merchnts/merchants-context';
import { SignUpSuccess } from '../components/sign-up/SignUpSuccess';
import { ApplicationViewSummary } from '../components/application-view/ApplicationViewSummary';
import { ApplicationViewBusinessDetails } from '../components/application-view/ApplicationViewBusinessDetails';
import {
	APPLICATION_VIEW_BASE_VIEW,
	APPLICATION_VIEW_STEPS,
} from '../components/application-view/ApplicationViewAsideMenu';
import { QuickApplication } from '../components/application/quick-application/QuickApplication';
import { applicationDetailsRoutes } from './applicationDetailsRoutes';
import { ApplicationDetails } from '../components/application/ApplicationDetails';
import { DisputesAlertProvidersList } from '../views/disputes-alert-provider/DisputesAlertProvidersList';
import { ROUTES } from './routesEnums';
import Platforms from '../views/platforms/Platforms';
import StripeSuccess from '../views/success/StripeSuccess';
import StripeDecline from '../views/decline/StripeDecline';
import { StripeSignUp } from '../views/sign-up/StripeSignUp';
import StripeCountryDecline from '../views/decline/StripeCountryDecline';
import { ProviderPage } from '../views/provider/ProviderPage';
import { BatchesPage } from '../views/batches/BatchesPage';
import { batchesRoutes } from './batchesRoutes';
import { providersRoutes } from './providersRoutes';
import { getSettlementsRoutes } from './settlementsRoutes';
import { SettlementsPage } from '../views/settlements/SettlementsPage';

const MainRouter = (): React.ReactElement => {
	const { t } = useTranslation();
	const userData = useSelector<RootState>(({ auth }) => auth.userData, shallowEqual);
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const applicationStatus = authUser?.userData?.applicationStatus;

	console.log('userData---', userData);

	// @ts-ignore
	return (
		<Router>
			{
				!userData && (
					<IdentityProvider>
						<MerchantsProvider>
							<Routes>
								<Route path="/auth/login" element={<Login />}/>
								{
									process.env.REACT_APP_ENVIRONMENT !== 'production' && (
										<>
											<Route path="/auth/registration" element={<SignUp />} />
											<Route path="/auth/registration/success" element={<SignUpSuccess />} />
										</>
									)
								}

								<Route path="/auth/stripe" element={<StripeSignUp />} />
								<Route path="/stripe-success" element={<StripeSuccess />} />
								<Route path="/stripe-decline" element={<StripeDecline />} />
								<Route path="/stripe-country-decline" element={<StripeCountryDecline />} />
								<Route path="/auth/confirm-password" element={<ConfirmAccount />}/>
								<Route path="/auth/confirm-forgot-password" element={<ConfirmForgotPassword />}/>
								<Route path="/auth/forgot-password" element={<ForgotPassword />}/>
								{/*<Route path="/auth/forgot-password" element={<ForgotPasswordNew />}/>*/}
								<Route path="/auth/reset-password" element={<ForgotPassword />}/>
								<Route path="/order-info" element={<CustomerPage />}/>

								<Route path="/" element={<Login />}/>
								<Route path="*" element={<NotFound />}/>
								{/*<Route path="*" element={<Navigate to="/auth/login"/>} />*/}
							</Routes>

						</MerchantsProvider>
					</IdentityProvider>
				)
			}

			{
				userData && (applicationStatus === STATUSES.DRAFT || applicationStatus === STATUSES.IN_REVIEW || applicationStatus === STATUSES.SUBMITTED) && (
					<RoutingLayout>
						<Routes>
							<Route path="/error" element={<ErrorBoundaryPage />} />
							<Route path="/not-found" element={<NotFound />} />

							<Route path="/" element={<Navigate to={`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.BUSINESS_DETAILS}`} />} />
							<Route path="/auth/login" element={<Navigate to={`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.BUSINESS_DETAILS}`} />} />

							<Route
								path={`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.BUSINESS_DETAILS}`}
								element={<ApplicationViewBusinessDetails />}
							/>
							<Route
								path={`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.SUMMARY}`}
								element={<ApplicationViewSummary />}
							/>

							<Route path="/agreements/merchant-factoring-agreement" element={<MerchantAgreement />} />

							<Route path="*" element={<NotFound />} />
						</Routes>
					</RoutingLayout>
				)
			}

			{
				userData && (applicationStatus === STATUSES.APPROVED || applicationStatus === undefined) && (
					<RoutingLayout>
						<Routes>
							<Route path="/error" element={<ErrorBoundaryPage />} />
							<Route path="/not-found" element={<NotFound />} />

							<Route path="/" element={<Navigate to={'/orders'} />} />
							<Route path="/auth/login" element={<Navigate to="/dashboard" />} />
							<Route path="/agreements/merchant-factoring-agreement" element={<MerchantAgreement />} />
							{/*<Route path="/auth/confirm-application" element={<ConfirmApplication />} />*/}

							<Route path="/dashboard" element={<Dashboard />} />

							<Route path="/stripe-success" element={<StripeSuccess />} />
							<Route path="/stripe-decline" element={<StripeDecline />} />
							<Route path="/stripe-country-decline" element={<StripeCountryDecline />} />

							{
								checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
									<>
										<Route path="/analytics" element={<AnalyticsPage />} />
									</>)
							}

							{
								checkRoles(authUser, CLAIMS.PARTNER_ADMIN) && (
									<>
										<Route path="/analytics" element={<AnalyticsPage />} />
									</>)
							}

							{/*{*/}
							{/*	checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN) && (*/}
							{/*		<>*/}
							{/*			<Route path="/analytics" element={<AnalyticsPage />} />*/}
							{/*		</>)*/}
							{/*}*/}

							<Route path="/orders">
								<>
									<Route path="/orders/:id" element={<OrderDetails />} />
									<Route path="/orders" element={<OrdersList />} />

									{checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
										<Route path={'/orders/:id/activity'} element={<ActivityLog />} />
									)}
								</>
							</Route>

							<Route path={ROUTES.USERS} element={<UsersList />} />
							<Route path={`${ROUTES.USERS}/:userId/${USER_TABS.USER_INFO}`} element={
								<UserDetails>
									<UserDetailsInformation />
								</UserDetails>
							} />
							<Route path={`/users/:userId/${USER_TABS.ACCOUNT_SECURITY}`} element={
								<UserDetails>
									<UserDetailsSecurity />
								</UserDetails>
							} />
							<Route path={`/users/:userId/${USER_TABS.ACCOUNT}`} element={
								<UserDetails>
									<UserDetailsAccount />
								</UserDetails>
							} />

							<Route path="/risk" element={<Navigate to="/risk/block-list" />} />
							<Route path="/risk/block-list" element={<RiskPage />} />
							<Route path="/risk/reserve-config" element={<FundsReserveConfigPage />} />

							<Route path="/notifications" element={<Notifications />} />

							<Route path="/profile" element={<Profile />} />
							{
								checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
									<>
										<Route path={`${ROUTES.DISPUTES}`} >
											<Route path={`${ROUTES.DISPUTES}${ROUTES.DISPUTES_ALL}`} element={<DisputesPage />} />
											<Route path={`${ROUTES.DISPUTES}${ROUTES.DISPUTES_QUEUES}`} element={<DisputesQueuePage />} />
											<Route path={`${ROUTES.DISPUTES}${ROUTES.DISPUTES_ARCHIVED}`} element={<DisputesArchivedPage />} />
											<Route path={`${ROUTES.DISPUTES}${ROUTES.DISPUTES_QUEUE}/:disputeQueueId`} element={<DisputesQueueItem />} />
											<Route path={`${ROUTES.DISPUTES}${ROUTES.DISPUTES_QUEUE_ITEM}/:disputeQueueItemId`} element={<DisputesQueueDetails />} />
											<Route path={`${ROUTES.DISPUTES}/*`} element={<Navigate to={`${ROUTES.DISPUTES}${ROUTES.DISPUTES_ALL}`} />} />
										</Route>

										<Route path="partners">
											<Route path="/partners" element={<PartnersList />} />
											{
												partnerDetailsRoutes.map(({ key, pathname, component }) => (
													<Route path={pathname} key={key} element={
														<PartnerDetails>
															{component}
														</PartnerDetails>
													}/>
												))
											}
											<Route path='/partners/*' element={<Navigate to="/partners"/>} />
										</Route>

										<Route path={ROUTES.AGENCIES} element={<Navigate to="/agencies/commissions" />} />
										<Route path={`${ROUTES.AGENCIES}${ROUTES.AGENCIES}`} element={<SalesAgenciesList />} />
										<Route path={`${ROUTES.AGENCIES}${ROUTES.AGENCIES}/:id`} element={<AgencyDetails />} />
										<Route path="/agencies/commissions" element={<CommissionsPage />} />
										<Route path="/agencies/commissions/partner/:partnerId" element={<PartnerCommissionsDetailsPage />} />
										<Route path="/agencies/commissions/agency/:agencyId" element={<AgencyCommissionsDetailsPage />} />
										<Route path="/agencies/deals" element={<DealsPage />} />
										<Route path="/agencies/deals/partner/:partnerId" element={<PartnersDealsDetailsPage />} />
										<Route path="/agencies/deals/agency/:agencyId" element={<AgenciesDealsDetailsPage />} />

										<Route path={ROUTES.CONTACTS} element={<ContactsList />}/>
										<Route path={`${ROUTES.CONTACTS}/:contactId`} element={<ContactDetails />} />

										<Route path="/release-notes" element={<ReleaseNotes />} />

										{
											adminRoutes.map(({ key, title, pathname, component, breadcrumbs }) => (
												<Route path={pathname} key={key} element={
													<>
														<PageTitle breadcrumbs={breadcrumbs}>{t(title)}</PageTitle>
														{component}
													</>
												}/>
											))
										}
										{
											providersRoutes.map(({ key, pathname, component }) => (
												<Route path={pathname} key={key} element={
													<ProviderPage>
														{component}
													</ProviderPage>
												}/>
											))
										}

										{/*<Route path={`${ ROUTES.ADMIN}${ ROUTES.PROVIDERS}`} element={<ProviderPage />} />*/}

										<Route path='/admin/*' element={<Navigate to="/admin/webhooks-activity"/>} />
									</>
								)
							}

							{
								checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && (
									<>

										<Route path="/accounts">

											{
												merchantRoutes
													.filter(({ claim }) => checkRoles(authUser, claim))
													.map(({ key, pathname, component }) => (
													<Route path={pathname} key={key} element={
														<MerchantPage>
															{component}
														</MerchantPage>
													}/>
												))
											}
											<Route path="/accounts/merchants" element={<MerchantsPage />} />
											<Route path="/accounts/applications" element={<ApplicationsPage />} />
											{
												applicationDetailsRoutes.map(({ key, pathname, component }) => (
													<Route path={pathname} key={key} element={
														<ApplicationDetails>
															{component}
														</ApplicationDetails>
													}/>
												))
											}
											<Route path="/accounts/applications/new-application" element={<NewApplication />} />
											<Route path="/accounts/applications/quick-application" element={<QuickApplication />} />
											<Route path="/accounts/applications/:id/quick-application" element={<QuickApplication />} />
											<Route path="/accounts/accounts" element={<Accounts />} />
											<Route path="/accounts/accounts/:accountId" element={<AccountDetails />} />
											<Route path='/accounts/*' element={<Navigate to="/accounts/accounts"/>} />
										</Route>

										<Route path={`${ROUTES.FUNDS}`} >
											{
												batchesRoutes.map(({ key, pathname, component }) => (
													<Route path={pathname} key={key} element={
														<BatchesPage>
															{component}
														</BatchesPage>
													}/>
												))
											}


											<Route path={`${ROUTES.FUNDS}${ROUTES.BATCHES}${ROUTES.POSTED}/:id`} element={<BatchDetails />} />
											<Route path={`${ROUTES.FUNDS}${ROUTES.BATCHES}${ROUTES.UNPOSTED}/:id`} element={<BatchDetails />} />
											<Route path={`${ROUTES.FUNDS}${ROUTES.BATCHES}${ROUTES.SHELVED}/:id`} element={<BatchDetails />} />
											{
												getSettlementsRoutes(checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN)).map(({ key, pathname, component }) => (
													<Route path={pathname} key={key} element={
														<SettlementsPage>
															{component}
														</SettlementsPage>
													}/>
												))
											}
											<Route path={`${ROUTES.FUNDS}${ROUTES.SETTLEMENTS}/:id`} element={<SettlementsDetails />} />

											<Route path="/funds/funds-reserve" element={<FundsReservePage />} />
											{/*<Route path="/funds/funds-reserve/:mid/details" element={<FundsReserveDetails />} />*/}
											<Route path={`${ROUTES.FUNDS}${ROUTES.FINANCE}`} element={<FinancialAccount />} />

											<Route path={`${ROUTES.FUNDS}/*`} element={<Navigate to={`${ROUTES.FUNDS}${ROUTES.BATCHES}${ROUTES.UNPOSTED}`} />} />
										</Route>
									</>
								)
							}

							{
								checkRoles(authUser, CLAIMS.MERCHANT_ADMIN) && (
									<>
										<Route path={`/user-settings/profile`} element={<ProfileSettings />} />
										{/*<Route path={`${url}/notifications`}>*/}
										{/*	<ProfileSettingsNotifications/>*/}
										{/*</Route>*/}
										<Route path={`/user-settings/business-settings`} element={<BusinessSettings />} />
									</>
								)
							}


							{
								!checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && (
									<>
										{
											developersRoutes(t).map(({ key, pathname, component }) => (
												<Route path={pathname} key={key} element={component} />
											))
										}
										{
											getSettlementsRoutes(checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN)).map(({ key, pathname, component }) => (
												<Route path={pathname} key={key} element={
													<SettlementsPage>
														{component}
													</SettlementsPage>
												}/>
											))
										}

										<Route path={`${ROUTES.SETTLEMENTS}/:id`} element={<SettlementsDetails />} />
									</>
								)
							}
										{/*{checkRoles(authUser, CLAIMS.MERCHANT_FINANCE) && (*/}
										{/*	<>*/}
										{/*		<Route path="/deals" element={<MainDeals />} />*/}
										{/*		<Route path="/commissions" element={<MainCommissions />} />*/}
										{/*	</>*/}
										{/*)}*/}


							{
                // TODO check partners permissions
                (checkRoles(authUser, CLAIMS.PARTNER_ADMIN) || checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN)) && (
									<>
										<Route path="/disputes" element={<DisputesPage />} />

										<Route path="/webhooks-activity" element={
											<>
												<PageTitle breadcrumbs={[]}>{t('MENU.WEBHOOKS_ACTIVITY')}</PageTitle>
												<Logs/>
											</>
										} />

										<Route path="/fee-configuration" element={
											<>
												<PageTitle
													breadcrumbs={[]}
													description={t('DEFAULT_FEE_CONFIG.PAGE_DESCRIPTION')}
												>
													{t('MENU.FEE_CONFIGURATION')}
												</PageTitle>
												<FeeConfiguration/>
											</>
										} />

										<Route path="/alert-providers" element={
											<>
												<PageTitle breadcrumbs={[]}>{t('MENU.DISPUTES_ALERT_PROVIDERS')}</PageTitle>
												<DisputesAlertProvidersList/>
											</>
										} />
									</>
								)
							}

							{
								// TODO check partners permissions
								checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN) && (
									<>
										<Route path="/disputes" element={<DisputesPage />} />

										<Route path="/webhooks-activity" element={
											<>
												<PageTitle breadcrumbs={[]}>{t('MENU.WEBHOOKS_ACTIVITY')}</PageTitle>
												<Logs/>
											</>
										} />

										<Route path="/fee-configuration" element={
											<>
												<PageTitle
													breadcrumbs={[]}
													description={t('DEFAULT_FEE_CONFIG.PAGE_DESCRIPTION')}
												>
													{t('MENU.FEE_CONFIGURATION')}
												</PageTitle>
												<FeeConfiguration/>
											</>
										} />
									</>
								)
							}

							{
								checkRoles(authUser, CLAIMS.MERCHANT_ADMIN) && (
									<>
										<Route path="/platforms" element={<Platforms />} />
										<Route path="/auth/stripe" element={<Navigate to={'/platforms'} />} />
									</>
								)
							}

							{/*{isAdmin && <Route path="/partners" component={PartnersList}/>}*/}

							<Route path="*" element={<Navigate to={'/not-found'} />} />
						</Routes>

					</RoutingLayout>
				)
			}

			{/*<Route path="/error" component={ErrorsPage}/>*/}
			<Routes>
				<Route path="/logout" element={<Logout />} />
			</Routes>
			<MasterInit />
		</Router>
	);
};

export { MainRouter };
